import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Dropdown, Input, MenuProps, theme, Typography } from 'antd'
import debounce from 'lodash/debounce'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import { SearchOutlined, UserOutlined } from '@ant-design/icons'
import SearchEmpty from '../../../Empty/SearchEmpty'
import Loading from '../../../Loading/GeneralLoading'
import { UserService } from '../../../../services/user-services'
import { UserData } from '../../../../types/user/types'
import { InfoCxt } from '../../../../App'
import { DEV_LOGIN_BYPASS } from '../../../../utils/devLoginBypass'

const { Text } = Typography
const { useToken } = theme

const DEBOUNCE_TIME = 500

const SearchBar: React.FC = () => {
  const navigate = useNavigate()

  const { token: themeToken } = useToken()
  const { role } = useContext(InfoCxt)
  const isAdmin = role === 'ROLE_ADMIN' || DEV_LOGIN_BYPASS

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [text, setText] = useState('')
  const [allPatients, setAllPatients] = useState<UserData[] | null>(null)
  const [options, setOptions] = useState<MenuProps['items']>([])

  const userService = UserService.getInstance()

  const ensurePatientsLoaded = useCallback(async () => {
    if (allPatients !== null) return allPatients
    const patients = (await userService.getAll()).filter(
      (patient) => isAdmin || (patient as any).otherInformation?.hiddenFromPortal !== true
    )
    setAllPatients(patients)
    return patients
  }, [allPatients, userService, isAdmin])

  // debounce text input, must use useMemo instead of useCallback
  const debounceSetText = useMemo(() => debounce(setText, DEBOUNCE_TIME), [setText])

  const handleClick = useCallback(
    ({ key }: { key: string }) => {
      navigate(`/patient/${key}`)
      setIsOpen(false)
      setOptions([])
      setText('')
    },
    [navigate]
  )

  useEffect(() => {
    if (!text) {
      setOptions([])
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)

    ensurePatientsLoaded().then((patients) => {
      if (cancelled) return
      const query = text.trim().toLowerCase()
      const matches = patients.filter((patient) => {
        const phone = patient.otherInformation?.phoneNumber ?? ''
        return (
          patient.userId?.toLowerCase().includes(query) ||
          patient.username?.toLowerCase().includes(query) ||
          patient.email?.toLowerCase().includes(query) ||
          phone.toLowerCase().includes(query)
        )
      })

      const opts: MenuProps['items'] = matches.slice(0, 20).map((patient) => ({
        key: patient.userId,
        label: patient.username || patient.email || patient.userId,
        icon: <UserOutlined />,
      }))
      setOptions(opts)
      setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [text, ensurePatientsLoaded])

  const contentStyle: React.CSSProperties = {
    backgroundColor: themeToken.colorBgElevated,
    borderRadius: themeToken.borderRadiusLG,
    boxShadow: themeToken.boxShadowSecondary,
    padding: '12px',
  }

  const menuStyle: React.CSSProperties = {
    boxShadow: 'none',
  }

  const LocalEmpty = React.memo(() => <SearchEmpty />)
  LocalEmpty.displayName = ''

  return (
    <>
      <Dropdown
        trigger={['click']}
        open={isOpen}
        onOpenChange={(flag: boolean) => {
          setIsOpen(flag)
        }}
        destroyPopupOnHide={false}
        menu={{ items: options, mode: 'vertical', onClick: handleClick }}
        dropdownRender={(menu) => (
          <div style={contentStyle}>
            <div style={{ padding: '4px 12px 8px 12px' }}>
              <Text strong>Search Results</Text>
            </div>
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {options?.length ? (
                  <>{React.cloneElement(menu as React.ReactElement, { style: menuStyle })}</>
                ) : (
                  <LocalEmpty />
                )}
              </>
            )}
          </div>
        )}>
        <Input
          allowClear
          placeholder='Search by patient UID, name, email, or phone number'
          prefix={<SearchOutlined style={{ color: '#9B9FA6' }} />}
          style={{ width: isMobile ? '100%' : '520px' }}
          onChange={(e) => {
            setIsOpen(true)
            const str = e.target.value as string
            if (str !== '') {
              setIsLoading(true)
              debounceSetText(str)
            } else {
              setIsLoading(false)
              setText('')
            }
          }}
        />
      </Dropdown>
    </>
  )
}

export default SearchBar
